import React, { useState, useEffect } from 'react';

interface SimulationConfig {
  infeedRate?: number;
  infeedVariance?: number;
  infeedWeightMultiplier?: number;
  targetYieldPercent?: number;
  activeStationCount?: number;
  infeedBatchSize?: number;
  outfeedRate?: number;
  outfeedVariance?: number;
  pocketDistribution?: number[];
  pocketVariance?: number;
}

interface SimulationConfigTabProps {
  device: any;
  lineNumber: number;
  stationNumber: number;
  isInfeed: boolean;
  onConfigUpdate: () => void;
}

const SimulationConfigTab: React.FC<SimulationConfigTabProps> = ({
  device,
  lineNumber,
  stationNumber,
  isInfeed,
  onConfigUpdate
}) => {
  const [config, setConfig] = useState<SimulationConfig>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [lineId, setLineId] = useState<string>('');

  useEffect(() => {
    fetchLineId();
  }, [lineNumber]);

  useEffect(() => {
    if (lineId) {
      loadConfig();
    }
  }, [lineId, stationNumber]);

  const fetchLineId = async () => {
    try {
      console.log('[SimulationConfigTab] Fetching line ID for lineNumber:', lineNumber);
      const response = await fetch('/api/lines');
      if (response.ok) {
        const data = await response.json();
        console.log('[SimulationConfigTab] Lines data:', data);
        const line = data.data?.find((l: any) => l.lineNumber === lineNumber);
        console.log('[SimulationConfigTab] Found line:', line);
        if (line) {
          setLineId(line.id);
          console.log('[SimulationConfigTab] Set lineId to:', line.id);
        } else {
          console.warn('[SimulationConfigTab] No line found for lineNumber:', lineNumber);
          setError(`No line found for line number ${lineNumber}`);
        }
      } else {
        console.error('[SimulationConfigTab] Failed to fetch lines:', response.status);
        setError('Failed to load line information');
      }
    } catch (error) {
      console.error('[SimulationConfigTab] Error fetching line ID:', error);
      setError('Failed to load line information');
    }
  };

  const loadConfig = async () => {
    try {
      console.log('[SimulationConfigTab] Loading config for lineId:', lineId, 'stationNumber:', stationNumber);
      setLoading(true);
      const response = await fetch(`/api/simulation-configs?lineId=${lineId}&stationNumber=${stationNumber}`);
      console.log('[SimulationConfigTab] Config response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('[SimulationConfigTab] Config data:', data);
        if (data.data && data.data.length > 0) {
          console.log('[SimulationConfigTab] Using existing config:', data.data[0].config);
          setConfig(data.data[0].config || {});
        } else {
          console.log('[SimulationConfigTab] No existing config, setting defaults for isInfeed:', isInfeed);
          // Set default values based on device type
          if (isInfeed) {
            setConfig({
              infeedRate: 5.0,
              infeedVariance: 0.2,
              infeedWeightMultiplier: 1.15,
              targetYieldPercent: 87,
              activeStationCount: 10,
              infeedBatchSize: 50
            });
          } else {
            setConfig({
              outfeedRate: 2.0,
              outfeedVariance: 0.15,
              pocketDistribution: [0.45, 0.35, 0.12, 0.08],
              pocketVariance: 0.1,
              targetYieldPercent: 87
            });
          }
        }
      } else {
        console.error('[SimulationConfigTab] Failed to load config:', response.status);
        setError('Failed to load simulation configuration');
      }
    } catch (error) {
      console.error('[SimulationConfigTab] Error loading simulation config:', error);
      setError('Failed to load simulation configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await fetch('/api/simulation-configs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lineId,
          stationNumber,
          deviceType: isInfeed ? 'infeed' : 'outfeed',
          config
        }),
      });

      if (response.ok) {
        setSuccess('Configuration saved successfully');
        onConfigUpdate();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving simulation config:', error);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key: keyof SimulationConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const updatePocketDistribution = (index: number, value: number) => {
    const newDistribution = [...(config.pocketDistribution || [0.45, 0.35, 0.12, 0.08])];
    newDistribution[index] = value / 100; // Convert percentage to decimal
    updateConfig('pocketDistribution', newDistribution);
  };

  console.log('[SimulationConfigTab] Rendering with loading:', loading, 'lineId:', lineId, 'isInfeed:', isInfeed);

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-2 text-sm text-slate-600">Loading configuration...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              {isInfeed ? 'Infeed' : 'Outfeed'} Simulation Configuration
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Configure simulation parameters for {isInfeed ? 'infeed weight generation' : 'outfeed pocket distribution'}.</p>
              <p className="mt-1">Device: {device?.device_name || 'Unknown'} | Line: {lineNumber || 'N/A'} | Station: {stationNumber === 0 ? 'Infeed' : stationNumber}</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <div className="text-sm text-green-800">{success}</div>
        </div>
      )}

      {isInfeed ? (
        // Infeed Configuration
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-slate-900">Infeed Weight Parameters</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Base Infeed Rate (lbs/minute)
              </label>
              <input
                type="number"
                step="0.1"
                value={config.infeedRate || 5.0}
                onChange={(e) => updateConfig('infeedRate', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-500">Rate per station (will be multiplied by active stations)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Weight Variance (±)
              </label>
              <input
                type="number"
                step="0.01"
                value={config.infeedVariance || 0.2}
                onChange={(e) => updateConfig('infeedVariance', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-500">Random variance factor (0.2 = ±20%)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Weight Multiplier
              </label>
              <input
                type="number"
                step="0.01"
                value={config.infeedWeightMultiplier || 1.15}
                onChange={(e) => updateConfig('infeedWeightMultiplier', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-500">Compensates for weight division across stations</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Target Yield (%)
              </label>
              <input
                type="number"
                step="1"
                value={config.targetYieldPercent || 87}
                onChange={(e) => updateConfig('targetYieldPercent', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-500">Expected yield percentage (87% = 13% waste)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Active Station Count
              </label>
              <input
                type="number"
                step="1"
                min="1"
                max="10"
                value={config.activeStationCount || 10}
                onChange={(e) => updateConfig('activeStationCount', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-500">Expected number of active stations</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Batch Size (lbs)
              </label>
              <input
                type="number"
                step="5"
                value={config.infeedBatchSize || 50}
                onChange={(e) => updateConfig('infeedBatchSize', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-500">Target weight per batch</p>
            </div>
          </div>
        </div>
      ) : (
        // Outfeed Configuration
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-slate-900">Outfeed Pocket Parameters</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Base Outfeed Rate (lbs/minute)
              </label>
              <input
                type="number"
                step="0.1"
                value={config.outfeedRate || 2.0}
                onChange={(e) => updateConfig('outfeedRate', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-500">Base rate per station for all pockets</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Weight Variance (±)
              </label>
              <input
                type="number"
                step="0.01"
                value={config.outfeedVariance || 0.15}
                onChange={(e) => updateConfig('outfeedVariance', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-500">Random variance factor (0.15 = ±15%)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Pocket Variance (±)
              </label>
              <input
                type="number"
                step="0.01"
                value={config.pocketVariance || 0.1}
                onChange={(e) => updateConfig('pocketVariance', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-500">Variance in pocket distribution</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Target Yield (%)
              </label>
              <input
                type="number"
                step="1"
                value={config.targetYieldPercent || 87}
                onChange={(e) => updateConfig('targetYieldPercent', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-500">Overall yield target for all pockets</p>
            </div>
          </div>

          <div className="space-y-3">
            <h5 className="text-md font-medium text-slate-800">Pocket Distribution</h5>
            <p className="text-sm text-slate-600">Configure the percentage distribution across 4 pockets (should total 100%)</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Pocket 1 (Main)', desc: 'e.g., Breast meat' },
                { label: 'Pocket 2 (Secondary)', desc: 'e.g., Tenders' },
                { label: 'Pocket 3 (Byproduct)', desc: 'e.g., Bones/trim' },
                { label: 'Pocket 4 (Waste)', desc: 'e.g., Fat/unusable' }
              ].map((pocket, index) => (
                <div key={index} className="space-y-1">
                  <label className="block text-xs font-medium text-slate-700">
                    {pocket.label}
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={Math.round(((config.pocketDistribution || [0.45, 0.35, 0.12, 0.08])[index] || 0) * 100)}
                    onChange={(e) => updatePocketDistribution(index, parseInt(e.target.value))}
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="text-xs text-slate-500">{pocket.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-slate-50 p-3 rounded">
              <p className="text-sm text-slate-600">
                Total: {Math.round((config.pocketDistribution || [0.45, 0.35, 0.12, 0.08]).reduce((sum, val) => sum + val, 0) * 100)}%
                {Math.abs(Math.round((config.pocketDistribution || [0.45, 0.35, 0.12, 0.08]).reduce((sum, val) => sum + val, 0) * 100) - 100) > 1 && (
                  <span className="text-amber-600 ml-2">⚠️ Should total 100%</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={loadConfig}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={saveConfig}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
};

export default SimulationConfigTab;